import React, { useContext, useState } from 'react';
import { IonToggle, IonIcon } from '@ionic/react';
import { settingsOutline, notificationsOutline } from 'ionicons/icons';
import { useAppData } from '../../context/appContext';
import AppContext from '../../components/AppContext';
import ErrorLoggingService from '../../service/ErrorLoggingService';
import './settings.css';

/**
 * Settings Page (SRS-WEB-067, SRS-WEB-056)
 * User preference settings for notifications.
 *
 * Rendered by both shells: the admin route /dashboard/settings and the
 * clinician full-screen takeover in ClinicianDashboard.
 *
 * The per-event switches are dependents of "Email Notifications", not
 * peers of it, so they are rendered as an indented, railed subgroup
 * rather than as more rows in the same flat list.
 */

// The switches that only apply while email delivery is on.
const EMAIL_EVENTS = [
  { key: 'messages', label: 'Messages', testId: 'TSETTINGS_MESSAGES_NOTIFICATIONS' },
  { key: 'battery', label: 'Low battery status', testId: 'TSETTINGS_BATTERY_NOTIFICATIONS' },
  { key: 'symptoms', label: 'Symptom uploads', testId: 'TSETTINGS_SYMPTOMS_NOTIFICATIONS' },
];

// `showTitle` — the admin shell hides its own header strip on Settings, so
// the page must title itself there. The clinician takeover already renders
// a "Settings" header, so it opts out rather than showing it twice.
const Settings = ({ showTitle = true }) => {
  const appData = useAppData();
  const { userData } = useContext(AppContext);
  const userDataSafe = userData || { userId: null };

  // Notification preferences (SRS-WEB-056, SRS-WEB-067)
  const [notificationPrefs, setNotificationPrefs] = useState(() => {
    const saved = localStorage.getItem('rms-notification-prefs');
    const defaults = {
      emailEnabled: true,
      messages: true,
      battery: true,
      symptoms: true
    };
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaults;
      }
    }
    return defaults;
  });

  const updateNotificationPref = (key, value) => {
    const updated = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(updated);
    localStorage.setItem('rms-notification-prefs', JSON.stringify(updated));
    ErrorLoggingService.info("Notification preference updated", {
      key,
      value,
      userId: userDataSafe?.userId || 'unknown'
    });
  };

  React.useEffect(() => {
    appData.setSelected("Settings");
  }, [appData]);

  const emailOn = notificationPrefs.emailEnabled;

  return (
    <div className="settings-page">
      {showTitle && (
        <header className="settings-page-header">
          <IonIcon icon={settingsOutline} />
          <h1 className="settings-page-title">Settings</h1>
        </header>
      )}

      {/* Notification Preferences (SRS-WEB-056, SRS-WEB-067) */}
      <section className="settings-section">
        <div className="settings-section-head">
          <IonIcon icon={notificationsOutline} />
          <h2 className="settings-section-title">Notification Preferences</h2>
        </div>

        <div className="settings-row settings-row-master">
          <div className="settings-row-text">
            <span className="settings-row-title" id="settings-email-master">
              Email Notifications
            </span>
            <span className="settings-row-sub">
              Master switch for everything listed below.
            </span>
          </div>
          {/* `ios` mode keeps the handle inside the track — the Material
              toggle's overhanging white handle disappears on white rows. */}
          <IonToggle
            mode="ios"
            data-testid="TSETTINGS_EMAIL_NOTIFICATIONS"
            aria-labelledby="settings-email-master"
            checked={emailOn}
            onIonChange={(e) => updateNotificationPref('emailEnabled', e.detail.checked)}
          />
        </div>

        {/* Dependents stay visible when the master is off — hiding them
            would hide the relationship — but read and behave as inert. */}
        <div
          className={`settings-subgroup${emailOn ? '' : ' is-muted'}`}
          role="group"
          aria-labelledby="settings-email-events-caption"
        >
          <p className="settings-subgroup-caption" id="settings-email-events-caption">
            Email me about
          </p>
          {EMAIL_EVENTS.map(({ key, label, testId }) => (
            <div className="settings-row settings-subrow" key={key}>
              <div className="settings-row-text">
                <span className="settings-row-title" id={`settings-event-${key}`}>
                  {label}
                </span>
              </div>
              <IonToggle
                mode="ios"
                data-testid={testId}
                aria-labelledby={`settings-event-${key}`}
                checked={notificationPrefs[key]}
                disabled={!emailOn}
                onIonChange={(e) => updateNotificationPref(key, e.detail.checked)}
              />
            </div>
          ))}
        </div>

        {!emailOn && (
          <p className="settings-muted-note">
            Email notifications are off — none of these will be sent.
          </p>
        )}
      </section>
    </div>
  );
};

export default Settings;
